import { FC, useCallback, useState } from "react";
import "./BuyJourneyNamePopup.scss";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AlertRhombus from "../../../../assets/icons/alert-rhombus.svg";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import CustomPopup from "../../../../components/CustomPopup/CustomPopup";
import CustomThreeDotsLoader from "../../../../components/CustomThreeDotsLoader/CustomThreeDotsLoader";
import { useLazyUpdateUserProfileDetailsQuery } from "../../../../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import { useAppSelector } from "../../../../redux-store/CustomReduxHooks";
import { saveTempLeadName } from "../../../../redux-store/reducers/CsAppSlice";
import { setError } from "../../../../redux-store/reducers/CsErrorSlice";
import { Colors } from "../../../../styles/constant";
import { API_STATUS_CODE } from "../../../../utils/ApiEndpoints";
import {
  validNameCharacterRegex,
  nameRegex,
  tabViewPoint,
  DEVICE_TYPE,
  GTAScreenType,
} from "../../../../utils/constants";
import useWindowSize from "../../../../hooks/useWindowSize";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import abcdLogo from "../../../../assets/icons/revamp-home-abcd-logo-icon.svg";
import useDeviceDetection from "../../../../hooks/useDeviceDetection";
import TriggerGTAEvent from "../../../../utils/GTA";
import {
  T_CenterBannerData,
  T_FooterBannerData,
  T_L2BannerData,
  T_MyTrackData,
  T_NewOfferingData,
  T_TopBannerData,
} from "../../../../features/Features_Cms/CmsGlobal.Types";
import {
  getLeadCreationFlags,
  usePlatformFlags,
} from "../../../../hooks/utils/bannerFilterUtils";
import { useAppTranslation } from "../../../../hooks/useAppTranslation";

interface BuyJourneyNamePopupProps {
  open: boolean;
  handleOpen: Function;
  cardsForJourneyPopup:
    | T_MyTrackData
    | T_NewOfferingData
    | T_TopBannerData
    | T_CenterBannerData
    | T_FooterBannerData
    | T_L2BannerData
    | undefined;

  handleQrPopupOpen?: Function;
  handleLeadJourney?: Function;
}
const BuyJourneyNamePopup: FC<BuyJourneyNamePopupProps> = ({
  open,
  handleOpen,
  cardsForJourneyPopup,
  handleQrPopupOpen,
  handleLeadJourney,
}) => {
  const { width } = useWindowSize();
  const { t } = useAppTranslation("common");
  const dispatch = useDispatch();
  const [name, setName] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [updateUserProfileDetailsApi] = useLazyUpdateUserProfileDetailsQuery();
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const userDetails = csAppSlice.userDetails;
  const deviceType = useDeviceDetection();
  const platformFlags = usePlatformFlags(
    csAppSlice.channel,
    csAppSlice.NativeDeviceInfo?.deviceName,
  );
  const isMobileBrowser = deviceType === DEVICE_TYPE.ABC_WEB_MOB_CS; //true when runs on mobile browser

  const handleClosePopup = useCallback(() => {
    handleOpen(false);
  }, [handleOpen]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (
      value.length !== 0 &&
      !value?.split("").every((char) => validNameCharacterRegex.test(char))
    ) {
      setNameError(t("home.buyJourney.validation.onlyAlphabets"));
      return;
    }
    setNameError("");
    setName(value);
  };

  const validateName = (value: string) => {
    if (value.trim() === "") {
      setNameError(t("home.buyJourney.validation.enterValidName"));
      return false;
    }
    // Check other validation criteria
    const isValid =
      value.length >= 2 &&
      value.length <= 50 &&
      nameRegex.test(value) &&
      (value.match(/ /g) || []).length <= 2 &&
      !/ {2}/.test(value);

    if (!isValid) {
      setNameError(t("home.buyJourney.validation.nameNotValid"));
    } else {
      setNameError("");
    }
    return isValid;
  };

  const handleProceed = () => {
    setBtnLoading(true);
    TriggerGTAEvent("serv_explore_abcd_offering_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "serv_homescreen",
      CTA_text: "proceed",
      isPrevPageNameRequired: true,
    });
    let reqPayload = {
      csUserId: csAppSlice.csUserId,
      name: userDetails?.name,
      pan: userDetails?.pan,
      dob: userDetails?.dob,
      mobile: userDetails?.mobileNumber,
      emailId: userDetails?.emailId,
      tempLeadName: name,
    };
    updateUserProfileDetailsApi(reqPayload).then((response) => {
      setBtnLoading(false);
      if (response.isError || response.data?.code !== API_STATUS_CODE.SUCCESS) {
        dispatch(
          setError({
            error: new Error("Error"),
            message: "Update UserProfile API failed",
          }),
        );
        return;
      }
      if (response?.data?.code === API_STATUS_CODE.SUCCESS) {
        dispatch(saveTempLeadName(name));
        handleClosePopup();

        const { isWebLead, isAppLead } =
          getLeadCreationFlags(cardsForJourneyPopup);
        let supportsLeadCreation: boolean | undefined;

        supportsLeadCreation = isWebLead || isAppLead;

        const isWebQrPopup = cardsForJourneyPopup?.attributes?.webQrPopup;
        if (
          platformFlags.isWeb &&
          !isMobileBrowser &&
          !supportsLeadCreation &&
          isWebQrPopup
        ) {
          handleQrPopupOpen?.();
        } else {
          handleLeadJourney?.(cardsForJourneyPopup, name);
        }
      }
    });
  };

  return (
    <CustomPopup
      className={
        width < tabViewPoint ? "buy-journey-popup" : "buy-journey-popup-web"
      }
      open={open}
      handleOpen={handleClosePopup}
      btnLabel={t("home.actionButtons.goBack")}
      btnStartIcon={
        <ChevronLeftIcon fontSize="small" sx={{ color: Colors.red }} />
      }
      transitionType="slide"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        padding={width < tabViewPoint ? "1rem 1rem 2rem 1rem" : "2rem 1.5rem"}
        borderRadius={width < tabViewPoint ? "0rem" : "2.5rem"}
        gap={"1.5rem"}
      >
        {width < tabViewPoint && (
          <Box
            sx={{
              width: "2rem",
              height: ".125rem",
              opacity: "25%",
              backgroundColor: Colors.textblack,
            }}
          />
        )}

        <Box
          display="flex"
          flexDirection="column"
          gap=".5rem"
          alignItems="center"
        >
          {width > tabViewPoint && (
            <img
              src={abcdLogo}
              style={{ width: "2.5625rem", height: "2.5625rem" }}
            />
          )}
          <Typography
            variant={width < tabViewPoint ? "h4" : "h3"}
            color={Colors.darkCharcoal}
            fontWeight={width < tabViewPoint ? 600 : 500}
            textTransform={width < tabViewPoint ? "uppercase" : "none"}
          >
            {t("home.buyJourney.title")}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={400}
            color={Colors.descriptionGray}
          >
            {t("home.buyJourney.subtitle")}
          </Typography>
        </Box>
        <TextField
          label={t("home.buyJourney.fullName")}
          value={name}
          variant="outlined"
          onBlur={(e) => validateName(e?.target?.value)}
          onChange={handleNameChange}
          error={nameError.length > 0}
          helperText={
            <span style={{ display: "flex", alignItems: "center" }}>
              <InputAdornment position="start">
                {nameError.length > 0 && <img src={AlertRhombus} />}
              </InputAdornment>
              {nameError}
            </span>
          }
          inputProps={{
            maxLength: 50,
            inputMode: "text",
          }}
          sx={{ width: width < tabViewPoint ? "100%" : "20.5rem" }}
        />
        {btnLoading ? (
          <CustomThreeDotsLoader />
        ) : (
          <Button
            variant="contained"
            onClick={handleProceed}
            disabled={isEmpty(name)}
            sx={{ width: width < tabViewPoint ? "100%" : "20.5rem" }}
          >
            {t("home.actionButtons.proceed")}
          </Button>
        )}
      </Box>
    </CustomPopup>
  );
};
export default BuyJourneyNamePopup;
